import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { AiOutlineFile } from 'react-icons/ai';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import { BiTrashAlt } from 'react-icons/bi';
import { colors, FILE_TYPES } from '../../../../constants';
import Text from 'components/styled/Text';
import { getFileSize } from 'utils/helpers';
import { useSelector } from 'react-redux';
import { fileLoadingSelector } from 'store/selectors';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const FileSlideContainer = styled.div`
  display: flex;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10;
`;

const FileImage = styled.img`
  width: ${props => !props.isBig && '48px'};
  height: ${props => !props.isBig && '48px'};
  max-width: ${props => props.isBig && '60vw'};
  max-height: ${props => props.isBig && '70vh'};
  border-radius: 4px;
  border: ${props => props.active && `1px solid ${colors.green}`};
  cursor: pointer;
`;

const FileVideo = styled.video`
  width: ${props => !props.isBig && '48px'};
  height: ${props => !props.isBig && '48px'};
  max-width: ${props => props.isBig && '60vw'};
  max-height: ${props => props.isBig && '70vh'};
  border-radius: 4px;
  border: ${props => props.active && `1px solid ${colors.green}`};
  cursor: pointer;
`;

const FileDocument = styled(AiOutlineFile)`
  width: ${props => (props.isBig ? '60vw' : '48px')};
  height: ${props => (props.isBig ? '70vh' : '48px')};
  border: ${props => props.active && `1px solid ${colors.green}`};
  cursor: pointer;
`;

const SwitchButton = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;

  svg {
    width: 64px;
    height: 64px;
  }
`;

const Slider = styled.div`
  flex: 3;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: flex-end;
`;

const CurrentSlideContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  height: 80vh;
`;

const SliderOptions = styled.div`
  display: flex;
  height: 15vh;
  gap: 12px;
`;

const CurrentSlide = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CurrentSlideInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TrashIcon = styled(BiTrashAlt)`
  width: 16px;
  height: 16px;
  fill: ${colors.grey80};
  :hover {
    fill: ${colors.red};
  }
`;

const FileSliderModal = ({ onClose, data, onDeleteFile }) => {
  const isLoading = useSelector(fileLoadingSelector);
  const { t } = useTranslation();
  const [currentItem, setCurrentItem] = useState(0);

  const handleChangeCurrentItem = index => event => {
    event.stopPropagation();
    if (data[index]) {
      setCurrentItem(index);
    }
  };

  useEffect(() => {
    if (isLoading) {
      toast.loading(t('file.slider.modal.toast.loading.message'));
    }
    if (!isLoading) {
      toast.dismiss();
    }
  }, [isLoading]); // eslint-disable-line

  const getFileSource = (item, index, isBig) => {
    switch (item.mimetype) {
      case FILE_TYPES.IMAGE:
        return (
          <FileImage
            onClick={handleChangeCurrentItem(index)}
            key={item.id}
            src={item.url}
            alt=""
            active={index === currentItem && !isBig}
            isBig={isBig}
          />
        );
      case FILE_TYPES.VIDEO:
        return (
          <FileVideo
            onClick={handleChangeCurrentItem(index)}
            key={item.id}
            src={item.url}
            controls={isBig}
            type={item.mimetype}
            active={index === currentItem && !isBig}
            isBig={isBig}
          />
        );
      default:
        return (
          <FileDocument
            onClick={handleChangeCurrentItem(index)}
            key={item.id}
            active={index === currentItem && !isBig}
            isBig={isBig}
          />
        );
    }
  };

  return createPortal(
    <FileSlideContainer onClick={onClose}>
      <SwitchButton onClick={handleChangeCurrentItem(currentItem - 1)}>
        <BsChevronLeft fill={colors.white} />
      </SwitchButton>
      <Slider>
        <CurrentSlideContainer>
          {getFileSource(data[currentItem], currentItem, true)}
          <CurrentSlide>
            <CurrentSlideInfo>
              <Text color={colors.white} variant="body-small">
                {data[currentItem].filename}
              </Text>
              <Text color={colors.white} variant="caption">
                {getFileSize(data[currentItem])}
              </Text>
            </CurrentSlideInfo>
            <TrashIcon onClick={onDeleteFile(data[currentItem]._id)} />
          </CurrentSlide>
        </CurrentSlideContainer>
        <SliderOptions>
          {data.map((item, index) => getFileSource(item, index))}
        </SliderOptions>
      </Slider>
      <SwitchButton onClick={handleChangeCurrentItem(currentItem + 1)}>
        <BsChevronRight fill={colors.white} />
      </SwitchButton>
    </FileSlideContainer>,
    document.body,
  );
};

FileSliderModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
  onDeleteFile: PropTypes.func.isRequired,
};

export default FileSliderModal;
